<template>
  <a-modal
    :title="`Bids for Load: ${loadTitle}`"
    :visible="visible"
    width="95%"
    :bodyStyle="{ padding: '16px' }"
    :mask-closable="true"
    :keyboard="true"
    :show-cancel="false"
    @cancel="handleClose"
    :footer="null"
  >
    <a-row :gutter="16">
      <!-- Left Side: Load Info + Bids Table -->
      <a-col :span="selectedBidderForChat ? 14 : 24">
        <!-- Load Information Header -->
        <a-card size="small" style="margin-bottom: 16px;" v-if="loadInfo && Object.keys(loadInfo).length">
          <template slot="title">
            <span>Load Information</span>
          </template>
          <a-row :gutter="16">
            <a-col :span="6">
              <p><strong>External Load ID:</strong> {{ (loadInfo && loadInfo.external_load_id) || 'N/A' }}</p>
            </a-col>
            <a-col :span="6">
              <p><strong>Bid ID:</strong> {{ (loadInfo && loadInfo.bid_id) || 'N/A' }}</p>
            </a-col>
            <a-col :span="6">
              <p><strong>Origin:</strong> {{ getLocationString(loadInfo && loadInfo.origin_city_name, loadInfo && loadInfo.origin_state_code) }}</p>
            </a-col>
            <a-col :span="6">
              <p><strong>Destination:</strong> {{ getLocationString(loadInfo && loadInfo.destination_city_name, loadInfo && loadInfo.destination_state_code) }}</p>
            </a-col>
          </a-row>
          <a-row :gutter="16">
            <a-col :span="6">
              <p><strong>Distance:</strong> {{ (loadInfo && loadInfo.total_distance) || 'N/A' }} miles</p>
            </a-col>
            <a-col :span="6">
              <p><strong>Pickup Date:</strong> {{ formatDate(loadInfo && loadInfo.scheduled_pickup_date_time) }}</p>
            </a-col>
            <a-col :span="6">
              <p><strong>Status:</strong>
                <a-tag :color="getStatusColor(loadInfo && loadInfo.status)">
                  {{ ((loadInfo && loadInfo.status) || '').toUpperCase() }}
                </a-tag>
              </p>
            </a-col>
            <a-col :span="6">
              <p><strong>Total Bids:</strong> {{ bids.length }}</p>
            </a-col>
          </a-row>
        </a-card>

        <!-- Bids Table -->
        <a-card title="Bids" size="small">
          <a-table
            :columns="bidColumns"
            :data-source="bids"
            :loading="loading"
            :pagination="paginationConfig"
            rowKey="id"
            size="small"
          >
            <!-- Bid Amount Column -->
            <template slot="offer_amount" slot-scope="offer_amount, record">
              <span :style="record.cancelled ? 'font-weight: bold; color: #ff4d4f;' : 'font-weight: bold; color: #1890ff;'">
                ${{ formatCurrency(offer_amount) }}
              </span>
            </template>

            <!-- Created At Column -->
            <template slot="created_at" slot-scope="created_at">
              {{ formatDate(created_at) }}
            </template>

            <!-- Bidder Column -->
            <template slot="bidder" slot-scope="bidder, record">
              <span style="display: flex; align-items: center;">
                <span
                  v-if="record.cancelled"
                  style="width: 8px; height: 8px; background-color: #ff4d4f; border-radius: 50%; margin-right: 8px; display: inline-block;"
                  title="Cancelled Bid"
                ></span>
                <span v-else style="width: 8px; height: 8px; background-color: #52c41a; border-radius: 50%; margin-right: 8px; display: inline-block;" title="Active Bid"></span>
                {{ bidder ? (bidder.full_name + ' (' + bidder.company.name + ')' || bidder.name || 'Unknown Bidder') : 'N/A' }}
              </span>
            </template>

            <!-- Status Column -->
            <template slot="status" slot-scope="status, record">
              <a-tag :color="record.cancelled ? 'red' : getBidStatusColor(status)">
                {{ record.cancelled ? 'CANCELLED' : (status || 'PENDING').toUpperCase() }}
              </a-tag>
            </template>

            <!-- Notes Column -->
            <template slot="notes" slot-scope="notes">
              <span v-if="notes && notes.trim()" :title="notes">
                {{ notes }}
              </span>
              <span v-else style="color: #999; font-style: italic;">
                No notes
              </span>
            </template>

            <!-- Chat Column -->
            <template slot="chat" slot-scope="text, record">
              <a-button
                type="link"
                size="small"
                @click="openChatWithBidder(record)"
                :class="{ 'active-chat-btn': isActiveChatBidder(record) }"
              >
                <a-badge :count="getChatUnreadCount(record)" :offset="[8, -4]">
                  <a-icon type="message" />
                </a-badge>
                Chat
              </a-button>
            </template>

            <!-- Action Column -->
            <template slot="action" slot-scope="text, record">
              <!-- Show Choose Winner button only for admin users -->
              <a-popconfirm
                v-if="isAdminUser"
                title="Are you sure you want to choose this bid as winner?"
                ok-text="Yes"
                cancel-text="No"
                @confirm="chooseWinner(record)"
              >
                <a-button type="primary" size="small" :disabled="loading || (record.status && record.status.toLowerCase() === 'accepted') || record.cancelled">
                  <a-icon type="trophy" />
                  Winner
                </a-button>
              </a-popconfirm>
            </template>
          </a-table>
        </a-card>
      </a-col>

      <!-- Right Side: Chat Panel -->
      <a-col :span="10" v-if="selectedBidderForChat">
        <a-card size="small" class="chat-card">
          <template slot="title">
            <div class="chat-header">
              <span>Chat with {{ selectedBidderForChat.bidder ? selectedBidderForChat.bidder.full_name : 'Bidder' }}</span>
              <a-button type="link" size="small" @click="closeChatPanel" icon="close" />
            </div>
          </template>

          <div class="chat-messages-area" ref="chatMessagesArea">
            <div v-if="chatLoading" class="chat-loading">
              <a-spin />
            </div>
            <div v-else-if="chatMessages.length === 0" class="no-messages">
              No messages yet. Start the conversation!
            </div>
            <div v-else>
              <div
                v-for="message in chatMessages"
                :key="message.id"
                class="message"
                :class="{ 'own': message.sender_type === 'admin' }"
              >
                <div class="message-content">
                  <div class="message-sender" v-if="message.sender_type !== 'admin'">
                    {{ message.sender_name }}
                  </div>
                  <div class="message-text">{{ message.message }}</div>
                  <div class="message-file" v-if="message.file_url">
                    <a :href="message.file_url" target="_blank" class="file-link">
                      <a-icon type="paper-clip" />
                      {{ message.file_name || 'Attachment' }}
                    </a>
                  </div>
                  <div class="message-time">
                    {{ formatMessageTime(message.created_at) }}
                    <a-icon v-if="message.sender_type === 'admin' && message.is_read" type="check" class="read-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="chat-input">
            <a-input
              v-model="newMessage"
              placeholder="Type a message..."
              @pressEnter="sendMessage"
              :disabled="sendingMessage"
            />
            <a-button
              type="primary"
              shape="circle"
              icon="arrow-right"
              @click="sendMessage"
              :loading="sendingMessage"
              :disabled="!newMessage.trim()"
            />
          </div>
        </a-card>
      </a-col>
    </a-row>

    <!-- Custom Cancel Button -->
    <div style="text-align: right; margin-top: 16px; padding-top: 16px; border-top: 1px solid #f0f0f0;">
      <a-button @click="handleClose" type="default">
        Close
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import bidAPI from '../../api/bid';
import loadAPI from '../../api/load';
import chatAPI from '../../api/chat';

// Bid table columns
const bidColumns = [
  {
    title: 'Bidder',
    dataIndex: 'bidder',
    key: 'bidder',
    scopedSlots: { customRender: 'bidder' },
    width: 180,
  },
  {
    title: 'Amount',
    dataIndex: 'offer_amount',
    key: 'offer_amount',
    scopedSlots: { customRender: 'offer_amount' },
    width: 100,
    sorter: (a, b) => (a.offer_amount || 0) - (b.offer_amount || 0),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    scopedSlots: { customRender: 'status' },
    width: 90,
    filters: [
      { text: 'Pending', value: 'pending' },
      { text: 'Accepted', value: 'accepted' },
      { text: 'Rejected', value: 'rejected' },
      { text: 'Cancelled', value: 'cancelled' },
    ],
    onFilter: (value, record) => {
      if (value === 'cancelled') {
        return record.cancelled === true;
      }
      return (record.status || 'pending').toLowerCase() === value;
    },
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    key: 'created_at',
    scopedSlots: { customRender: 'created_at' },
    width: 130,
    sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
  },
  {
    title: 'Chat',
    dataIndex: 'chat',
    key: 'chat',
    scopedSlots: { customRender: 'chat' },
    width: 80,
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    scopedSlots: { customRender: 'action' },
    width: 100,
  },
];

export default {
  name: 'BidsPopup',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loadId: {
      type: String,
      default: null,
    },
    loadInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      bids: [],
      loading: false,
      bidColumns,
      // Chat state
      selectedBidderForChat: null,
      chatMessages: [],
      chatLoading: false,
      newMessage: '',
      sendingMessage: false,
      chatUnreadCounts: {},
    };
  },
  computed: {
    loadTitle() {
      const info = this.loadInfo || {};
      return info.external_load_id || info.bid_id || 'Unknown';
    },
    paginationConfig() {
      return {
        pageSize: 10,
        showSizeChanger: false,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bids`,
      };
    },

    // Check if current user is admin
    isAdminUser() {
      return localStorage.getItem('userType') === 'admin';
    },
  },
  methods: {
    async fetchBids() {
      if (!this.loadId) return;

      this.loading = true;
      try {
        const response = await bidAPI.getBidsByLoadId(this.loadId);
        this.bids = response.data || response || [];
        // Fetch chat unread counts for each bidder
        this.fetchChatUnreadCounts();
      } catch (error) {
        console.error('Error fetching bids:', error);
        this.bids = [];
        this.$message.error('Failed to load bids for this load');
      } finally {
        this.loading = false;
      }
    },

    async fetchChatUnreadCounts() {
      for (const bid of this.bids) {
        if (bid.bidder?.id) {
          try {
            const response = await chatAPI.getChatUnreadCount(this.loadId, bid.bidder.id);
            this.$set(this.chatUnreadCounts, bid.bidder.id, response.data?.count || 0);
          } catch (error) {
            console.error('Error fetching chat unread count:', error);
          }
        }
      }
    },

    getChatUnreadCount(record) {
      if (!record.bidder?.id) return 0;
      return this.chatUnreadCounts[record.bidder.id] || 0;
    },

    isActiveChatBidder(record) {
      return this.selectedBidderForChat &&
             this.selectedBidderForChat.bidder?.id === record.bidder?.id;
    },

    async openChatWithBidder(bid) {
      if (!bid.bidder?.id) {
        this.$message.error('Cannot open chat: bidder information not available');
        return;
      }

      this.selectedBidderForChat = bid;
      this.chatMessages = [];
      this.chatLoading = true;

      try {
        const response = await chatAPI.getMessages(this.loadId, bid.bidder.id);
        this.chatMessages = response.data?.messages || [];

        // Mark messages as read
        await chatAPI.markAsRead(this.loadId, bid.bidder.id);
        this.$set(this.chatUnreadCounts, bid.bidder.id, 0);

        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (error) {
        console.error('Error fetching chat messages:', error);
        this.chatMessages = [];
      } finally {
        this.chatLoading = false;
      }
    },

    closeChatPanel() {
      this.selectedBidderForChat = null;
      this.chatMessages = [];
      this.newMessage = '';
    },

    async sendMessage() {
      if (!this.newMessage.trim() || !this.selectedBidderForChat || this.sendingMessage) return;

      this.sendingMessage = true;
      try {
        const response = await chatAPI.sendMessage(
          this.loadId,
          this.selectedBidderForChat.bidder.id,
          this.newMessage.trim()
        );

        // Add message to local array
        if (response.data?.message) {
          this.chatMessages.push(response.data.message);
        }

        this.newMessage = '';
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (error) {
        console.error('Error sending message:', error);
        this.$message.error('Failed to send message');
      } finally {
        this.sendingMessage = false;
      }
    },

    scrollChatToBottom() {
      const container = this.$refs.chatMessagesArea;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    async chooseWinner(bid) {
      if (!this.loadId || !bid || !bid.id) {
        this.$message.error('Invalid load or bid');
        return;
      }
      this.loading = true;
      try {
        console.log('Calling setLoadWinner API with:', this.loadId, bid.id);
        const response = await loadAPI.setLoadWinner(this.loadId, bid.id);
        console.log('setLoadWinner API response:', response);
        this.$message.success('Winner selected successfully');
        // Refresh bids to reflect updated status
        await this.fetchBids();
      } catch (error) {
        console.error('Error setting winner:', error);
        const message = (error && error.message) || 'Failed to set winner';
        this.$message.error(message);
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.closeChatPanel();
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(offer_amount) {
      if (!offer_amount) return '0.00';
      return parseFloat(offer_amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getLocationString(city, state) {
      if (!city && !state) return 'N/A';
      if (!city) return state;
      if (!state) return city;
      return `${city}, ${state}`;
    },

    getStatusColor(status) {
      const statusColors = {
        'pending': 'orange',
        'active': 'blue',
        'completed': 'brown',
        'cancelled': 'red',
        'in_progress': 'purple',
        'auction_lost': 'red',
        'auction_won': 'green',
        'removed_from_auction': 'volcano'
      };
      return statusColors[(status || '').toLowerCase()] || 'default';
    },

    getBidStatusColor(status) {
      const statusColors = {
        'pending': 'orange',
        'accepted': 'green',
        'rejected': 'red',
        'cancelled': 'volcano',
      };
      return statusColors[(status || 'pending').toLowerCase()] || 'default';
    },

    setupWebSocketListeners() {
      if (this.$wsOn) {
        this.$wsOn('chat_message', this.handleNewMessage);
      }
    },

    removeWebSocketListeners() {
      if (this.$wsOff) {
        this.$wsOff('chat_message', this.handleNewMessage);
      }
    },

    handleNewMessage(payload) {
      // Check if this message is for the currently open chat
      if (this.selectedBidderForChat &&
          payload.load_id === this.loadId &&
          payload.user_id === this.selectedBidderForChat.bidder?.id) {
        this.chatMessages.push(payload.message);
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
        // Mark as read since we're viewing this chat
        if (payload.message.sender_type !== 'admin') {
          chatAPI.markAsRead(this.loadId, this.selectedBidderForChat.bidder.id);
        }
      } else if (payload.load_id === this.loadId) {
        // Update unread count for other bidders
        const bidderId = payload.user_id;
        if (bidderId && payload.message.sender_type !== 'admin') {
          const currentCount = this.chatUnreadCounts[bidderId] || 0;
          this.$set(this.chatUnreadCounts, bidderId, currentCount + 1);
        }
      }
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.loadId) {
        this.fetchBids();
        this.setupWebSocketListeners();
      } else {
        this.closeChatPanel();
        this.removeWebSocketListeners();
      }
    },
    loadId(newVal) {
      if (newVal && this.visible) {
        this.fetchBids();
      }
    },
  },
  mounted() {
    if (this.visible && this.loadId) {
      this.setupWebSocketListeners();
    }
  },
  beforeDestroy() {
    this.removeWebSocketListeners();
  },
};
</script>

<style lang="scss" scoped>
.ant-card {
  .ant-card-body {
    p {
      margin-bottom: 4px;

      strong {
        color: #1890ff;
      }
    }
  }
}

.chat-card {
  height: 100%;
  min-height: 500px;
  display: flex;
  flex-direction: column;

  :deep(.ant-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 12px !important;
  }
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  min-height: 300px;
  max-height: 350px;
}

.chat-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.no-messages {
  text-align: center;
  color: #8c8c8c;
  padding: 24px;
}

.message {
  display: flex;
  margin-bottom: 12px;
}

.message.own {
  justify-content: flex-end;
}

.message-content {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message.own .message-content {
  background: #1890ff;
  color: #fff;
}

.message-sender {
  font-size: 11px;
  color: #1890ff;
  margin-bottom: 4px;
  font-weight: 500;
}

.message-text {
  word-break: break-word;
  font-size: 13px;
}

.message-file {
  margin-top: 8px;
}

.file-link {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-size: 12px;
  color: inherit;
}

.message.own .file-link {
  background: rgba(255, 255, 255, 0.2);
}

.file-link .anticon {
  margin-right: 4px;
}

.message-time {
  font-size: 10px;
  color: #8c8c8c;
  margin-top: 4px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.message.own .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.read-icon {
  margin-left: 4px;
  font-size: 10px;
}

.chat-input {
  display: flex;
  align-items: center;
  padding: 12px 0 0;
  gap: 8px;
  margin-top: auto;
}

.chat-input .ant-input {
  flex: 1;
  border-radius: 20px;
}

.active-chat-btn {
  color: #1890ff !important;
  font-weight: 600;
}

// Responsive adjustments
@media (max-width: 768px) {
  :deep(.ant-modal) {
    width: 95% !important;
    margin: 10px;
  }
}
</style>
